import React, { useState, useEffect } from 'react';
import { Search, Ship, Clock, MapPin, Layers } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';

export const TopBar = ({ title }) => {
  const { 
    vessels, 
    searchQuery, 
    setSearchQuery, 
    searchVessels, 
    selectVessel,
    mapStyle,
    setMapStyle,
    sidebarCollapsed
  } = useApp();
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (searchQuery.length > 0) {
      const results = searchVessels(searchQuery);
      setSearchResults(results.slice(0, 5));
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, searchVessels]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectVessel = (vessel) => {
    selectVessel(vessel);
    setSearchQuery('');
    setShowResults(false);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('id-ID', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <header 
      className={cn(
        "fixed top-0 right-0 z-30 h-16 bg-card border-b border-border transition-all duration-300",
        sidebarCollapsed ? "left-[70px]" : "left-[260px]"
      )}
    >
      <div className="flex items-center justify-between h-full px-6">
        {/* Left Section - Title & Search */}
        <div className="flex items-center gap-6">
          <h1 className="text-lg font-semibold text-foreground font-display">
            {title || 'Dashboard'}
          </h1>
          
          {/* Search */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari MMSI / Nama Kapal..."
                className="pl-10 w-[280px] bg-background"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => searchQuery && setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
              />
            </div>
            
            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-full bg-card rounded-lg border border-border shadow-lg z-50 overflow-hidden animate-slide-up">
                {searchResults.map((vessel) => (
                  <button
                    key={vessel.id}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-accent transition-colors text-left"
                    onClick={() => handleSelectVessel(vessel)}
                  >
                    <Ship className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{vessel.name}</p>
                      <p className="text-xs text-muted-foreground">
                        MMSI: {vessel.mmsi} | {vessel.shipType}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {vessel.status}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Stats & Controls */}
        <div className="flex items-center gap-6">
          {/* Map Style Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Layers className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {mapStyle === 'osm' ? 'Standard' : 'Satellite'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Map Style</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setMapStyle('osm')}>
                <MapPin className="w-4 h-4 mr-2" />
                Standard (OSM)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setMapStyle('satellite')}>
                <Layers className="w-4 h-4 mr-2" />
                Satellite
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Total Vessels */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-accent rounded-lg">
            <Ship className="w-4 h-4 text-accent-foreground" />
            <span className="text-sm font-medium text-accent-foreground">
              {vessels.length} Kapal
            </span>
          </div>

          {/* Date & Time */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span className="font-medium">{formatTime(currentTime)}</span>
            <span className="text-xs hidden md:inline">| {formatDate(currentTime)}</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
